import { create } from 'zustand';
import { packageAPI } from '../services/api';
import { Package, PromotionDetails } from '@shared/types';
import { getCachedData, setCachedData } from '../utils/cache';

interface PackagesCacheData {
  packages: Package[];
  promotionDetails: PromotionDetails | null;
}

interface PackagesStoreState {
  packages: Package[];
  promotionDetails: PromotionDetails | null;
  loading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  fetchPackages: () => Promise<PackagesCacheData | null>;
}

let pendingFetch: Promise<PackagesCacheData | null> | null = null;

const CACHE_KEY = 'packages';
const CACHE_TTL = 60000;

export const usePackagesStore = create<PackagesStoreState>((set, get) => ({
  packages: getCachedData<PackagesCacheData>(CACHE_KEY)?.packages || [],
  promotionDetails: getCachedData<PackagesCacheData>(CACHE_KEY)?.promotionDetails || null,
  loading: false,
  error: null,
  lastFetchTime: null,

  fetchPackages: async () => {
    const state = get();
    const now = Date.now();
    
    if (state.lastFetchTime && (now - state.lastFetchTime) < CACHE_TTL && state.packages.length > 0) {
      return { packages: state.packages, promotionDetails: state.promotionDetails };
    }

    if (pendingFetch) {
      return pendingFetch;
    }

    set({ loading: true, error: null });

    pendingFetch = (async () => {
      try {
        const response = await packageAPI.getAll();
        const apiData = (response.data as any)?.data;

        let packages: Package[] = [];
        let promotionDetails: PromotionDetails | null = null;

        if (apiData && apiData.packages && Array.isArray(apiData.packages)) {
          packages = apiData.packages;
          promotionDetails = apiData.promotionDetails || null;
        } else if (response.data && Array.isArray(response.data)) {
          packages = response.data;
        }

        const newData: PackagesCacheData = { packages, promotionDetails };
        setCachedData(CACHE_KEY, newData);

        set({
          packages,
          promotionDetails,
          loading: false,
          error: null,
          lastFetchTime: Date.now(),
        });

        return newData;
      } catch (err) {
        console.error('Failed to fetch packages:', err);
        const cachedData = getCachedData<PackagesCacheData>(CACHE_KEY);
        
        if (cachedData) {
          set({
            packages: cachedData.packages,
            promotionDetails: cachedData.promotionDetails,
            loading: false,
            error: null,
          });
          return cachedData;
        }

        set({ loading: false, error: 'Failed to fetch packages' });
        return null;
      } finally {
        pendingFetch = null;
      }
    })();

    return pendingFetch;
  },
}));
